import {
  ADULTS_KEY,
  DEFAULT_STAGE,
  getStage,
  isStageKey,
  type AudienceKey,
  type StageKey,
} from '@yoyoclub/shared';

import { resolveTimePreset } from '../../utils/whenRange';

/**
 * The homepage selection (docs/prd-homepage-redesign.md §3.1, §3.5) and the links it
 * produces. The selection lives in the URL hash, e.g. `/#stage=s2&when=weekend&regions=SOUTH_BAY`,
 * so a shared or reloaded page shows the same recommendations.
 */

export const HOME_TIMES = ['anytime', 'today', 'weekend', 'next7days', 'thisMonth'] as const;
export type HomeTime = (typeof HOME_TIMES)[number];

export const HOME_REGIONS = [
  'SOUTH_BAY',
  'PENINSULA',
  'EAST_BAY',
  'SAN_FRANCISCO',
  'OTHER',
] as const;
export type HomeRegion = (typeof HOME_REGIONS)[number];

export interface HomeSelection {
  audience: AudienceKey;
  when: HomeTime;
  regions: HomeRegion[];
}

export const DEFAULT_SELECTION: HomeSelection = {
  audience: DEFAULT_STAGE,
  when: 'anytime',
  regions: [],
};

const isHomeTime = (v: unknown): v is HomeTime =>
  typeof v === 'string' && (HOME_TIMES as readonly string[]).includes(v);
const isHomeRegion = (v: unknown): v is HomeRegion =>
  typeof v === 'string' && (HOME_REGIONS as readonly string[]).includes(v);

/** Unknown or missing values fall back to the defaults (stage s2, any time, all areas). */
export function parseSelection(hash: string): HomeSelection {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const stage = params.get('stage');
  const when = params.get('when');
  const regions = (params.get('regions') ?? '')
    .split(',')
    .filter(isHomeRegion)
    .filter((r, i, all) => all.indexOf(r) === i);
  return {
    audience: stage === ADULTS_KEY || isStageKey(stage) ? (stage as AudienceKey) : DEFAULT_STAGE,
    when: isHomeTime(when) ? when : 'anytime',
    regions: HOME_REGIONS.filter((r) => regions.includes(r)),
  };
}

export function selectionHash(sel: HomeSelection): string {
  const params = new URLSearchParams();
  params.set('stage', sel.audience);
  if (sel.when !== 'anytime') params.set('when', sel.when);
  if (sel.regions.length > 0) params.set('regions', sel.regions.join(','));
  // Keep the commas readable; URLSearchParams would encode them.
  return `#${params.toString().replace(/%2C/g, ',')}`;
}

export function isFiltered(sel: HomeSelection): boolean {
  return sel.when !== 'anytime' || sel.regions.length > 0;
}

/** PT dates (YYYY-MM-DD, inclusive) of the chosen time option, resolved against today. */
export function selectionWindow(sel: HomeSelection, now: Date = new Date()) {
  return resolveTimePreset(sel.when, {}, now);
}

/** Query string for GET /home/recommendations. */
export function recommendationsQuery(sel: HomeSelection, now: Date = new Date()): string {
  const params = new URLSearchParams();
  if (sel.audience === ADULTS_KEY) params.set('audience', ADULTS_KEY);
  else params.set('stage', sel.audience);
  const range = selectionWindow(sel, now);
  if (range.from) params.set('dateFrom', range.from);
  if (range.to) params.set('dateTo', range.to);
  if (sel.regions.length > 0) params.set('regions', sel.regions.join(','));
  return params.toString();
}

function appendWhenWhere(params: URLSearchParams, sel: HomeSelection) {
  if (sel.when !== 'anytime') params.set('when', sel.when);
  if (sel.regions.length > 0) params.set('regions', sel.regions.join(','));
}

/**
 * S7: "查看全部" for the activity row keeps the age (stage range, or adult-only),
 * the time option and the places. s4 has no upper bound, so no ageMax.
 */
export function activitiesLink(sel: HomeSelection): string {
  const params = new URLSearchParams();
  if (sel.audience === ADULTS_KEY) {
    params.set('participation', 'ADULT_ONLY');
  } else {
    const stage = getStage(sel.audience as StageKey);
    params.set('ageMin', String(stage.lo));
    if (Number.isFinite(stage.hi)) params.set('ageMax', String(stage.hi));
  }
  appendWhenWhere(params, sel);
  return `/activities?${params.toString().replace(/%2C/g, ',')}`;
}

/** S7: "查看全部" for the Picks row. */
export function picksLink(sel: HomeSelection): string {
  const params = new URLSearchParams();
  if (sel.audience === ADULTS_KEY) params.set('audience', ADULTS_KEY);
  else params.set('stage', sel.audience);
  appendWhenWhere(params, sel);
  return `/picks?${params.toString().replace(/%2C/g, ',')}`;
}

/** Display colors of each choice (prototype STAGES / PARENTS). */
export const AUDIENCE_THEME: Record<
  AudienceKey,
  { c: string; on: string; tint: string; dk: string; age?: string }
> = {
  s0: { c: '#3A7D44', on: '#fff', tint: '#E6F2E7', dk: '#2A5A32', age: '0-2' },
  s1: { c: '#5AC8FA', on: '#16323F', tint: '#E2F4FC', dk: '#1C6E95', age: '2-3' },
  s2: { c: '#FFD93D', on: '#3A2F00', tint: '#FFF5D1', dk: '#7A5D00', age: '3-5' },
  s3: { c: '#FF8A3D', on: '#3D1A00', tint: '#FFEADB', dk: '#A44A12', age: '5-8' },
  s4: { c: '#7A5B47', on: '#fff', tint: '#F0E8E1', dk: '#5B4232', age: '8+' },
  adults: { c: '#2C2C2C', on: '#fff', tint: '#EFE9E1', dk: '#2C2C2C' },
};
